/**
 * blogCtaTarget — à quelle page produit renvoyer le lecteur d'un article.
 *
 * ═══ POURQUOI CE FICHIER EXISTE ═══════════════════════════════════════════
 *
 * Mesuré en production sur 60 jours, le même sujet traité à deux endroits :
 *
 *   /blog/organiser-loto-associatif-regles-2026   49 visites →  0 inscription
 *   /projets/loto                                 28 visites →  4  (14,3 %)
 *   /blog/organiser-tombola-association-regles    30 visites →  0 inscription
 *   /projets/tombola                              72 visites → 21  (29,2 %)
 *
 * Et surtout : **591 sessions arrivent sur un article et repartent sans voir
 * une seule autre page.** Seules DEUX ont trouvé le chemin du blog vers
 * /projets/tombola. Le pont n'existait pas.
 *
 * Le blog attire des requêtes « je cherche un document » (modèle de PV,
 * plan comptable, règles du loto). Le lecteur repart avec son modèle. Le seul
 * appel à l'action de fin d'article était générique — « Lancez votre collecte
 * avec Donoor » — alors que le besoin du moment était « j'organise un loto ».
 *
 * Ce module fait correspondre chaque article à la page qui répond à SON besoin,
 * avec une promesse écrite pour ce besoin-là. Jamais vers la page d'accueil,
 * qui convertit deux à cinq fois moins bien que les pages de besoin.
 *
 * ═══ COMMENT ÇA MARCHE ════════════════════════════════════════════════════
 *
 * Des RÈGLES ORDONNÉES sur le slug de l'article, première correspondance
 * gagnante. Les articles à venir sont donc couverts sans rien recâbler, et le
 * défaut est `/collecte-dons-association` (18,9 % de passage vers
 * l'inscription) et non `/` (6,2 %).
 *
 * Les destinations sont un catalogue séparé : plusieurs règles peuvent pointer
 * vers la même page sans dupliquer son texte.
 */

export interface BlogCtaDestination {
  /** Chemin de la page produit. Jamais `/` ni `/register` directement. */
  to: string;
  /** Titre du bloc — nomme le besoin traité par l'article, pas le produit. */
  titre: string;
  /** Deux phrases maximum : ce que la personne obtient concrètement. */
  texte: string;
  /** Libellé du bouton — dit ce qui se passe, jamais « En savoir plus ». */
  bouton: string;
}

/**
 * Catalogue des destinations. La clé n'apparaît jamais à l'écran, elle sert
 * seulement à ce que plusieurs règles partagent un même texte.
 */
const DESTINATIONS = {
  tombola: {
    to: "/projets/tombola",
    titre: "Organisez votre tombola avec Donoor",
    texte:
      "Encaissement sur le stand, billets numérotés envoyés par e-mail avec QR code, liste des billets exportable pour le tirage. 0 € d'abonnement.",
    bouton: "Voir comment faire",
  },
  loto: {
    to: "/projets/loto",
    titre: "Organisez votre loto avec Donoor",
    texte:
      "Billets d'entrée avec QR code, contrôle des entrées et encaissement sur place depuis un téléphone, liste des participants à jour. Le jeu a lieu en salle.",
    bouton: "Voir comment faire",
  },
  kermesse: {
    to: "/projets/kermesse",
    titre: "Organisez votre kermesse avec Donoor",
    texte:
      "Billets, buvette et stands encaissés depuis un téléphone, sans terminal de paiement. Les fonds arrivent directement sur le compte de l'association.",
    bouton: "Voir comment faire",
  },
  gala: {
    to: "/projets/gala",
    titre: "Organisez votre gala avec Donoor",
    texte:
      "Billetterie en ligne, tables et tarifs multiples, reçus fiscaux automatiques pour les dons de la soirée. 0 € d'abonnement.",
    bouton: "Voir comment faire",
  },
  videGrenier: {
    to: "/projets/vide-grenier",
    titre: "Organisez votre vide-grenier avec Donoor",
    texte:
      "Inscriptions des exposants en ligne, emplacements payés d'avance, encaissement sur place par carte. Sans commission.",
    bouton: "Voir comment faire",
  },
  concert: {
    to: "/projets/concert",
    titre: "Organisez votre concert avec Donoor",
    texte:
      "Billetterie en ligne avec QR code, contrôle des entrées depuis un téléphone, plusieurs tarifs possibles. 0 % de commission.",
    bouton: "Voir comment faire",
  },
  billetterie: {
    to: "/billetterie",
    titre: "Votre billetterie associative, gratuite",
    texte:
      "Créez l'événement, vendez les billets en ligne, scannez les entrées depuis votre téléphone. Sans abonnement ni commission.",
    bouton: "Découvrir la billetterie",
  },
  adhesion: {
    to: "/adhesion",
    titre: "Gérez vos adhésions en ligne",
    texte:
      "Bulletins d'adhésion remplis en ligne, cotisations encaissées automatiquement, fichier des membres toujours à jour et carte d'adhérent à QR code.",
    bouton: "Découvrir les adhésions",
  },
  recusFiscaux: {
    to: "/recus-fiscaux",
    titre: "Des reçus fiscaux générés tout seuls",
    texte:
      "Le reçu Cerfa part automatiquement après chaque don, numéroté et conforme. Plus de tableur à tenir en fin d'année.",
    bouton: "Voir les reçus fiscaux",
  },
  qrCodes: {
    to: "/qr-codes",
    titre: "Collectez sur place avec un QR code",
    texte:
      "Un QR code à afficher ou à imprimer : le donateur scanne, paie en quelques secondes, et reçoit son reçu. Suivi des scans inclus.",
    bouton: "Découvrir les QR codes",
  },
  tapToPay: {
    to: "/tap-to-pay",
    titre: "Encaissez sans terminal de paiement",
    texte:
      "Votre iPhone devient le terminal : le donateur approche sa carte ou son téléphone, c'est encaissé. Aucun matériel à acheter ni à louer.",
    bouton: "Découvrir Tap to Pay",
  },
  donRecurrent: {
    to: "/don-recurrent-association",
    titre: "Mettez en place le don mensuel",
    texte:
      "Le don récurrent transforme des dons ponctuels en revenu prévisible. Mise en place en quelques minutes, le donateur gère lui-même son abonnement.",
    bouton: "Découvrir le don récurrent",
  },
  cagnotte: {
    to: "/cagnotte",
    titre: "Lancez votre cagnotte en ligne",
    texte:
      "Une page de collecte à votre nom, partageable partout, avec objectif et jauge de progression. Les fonds arrivent directement sur votre compte.",
    bouton: "Créer une cagnotte",
  },
  logiciel: {
    to: "/logiciel-association",
    titre: "Le logiciel qui tient votre association à jour",
    texte:
      "Membres, cotisations, dons, reçus fiscaux et suivi de trésorerie au même endroit. Gratuit, sans abonnement, hébergé dans l'Union européenne.",
    bouton: "Découvrir le logiciel",
  },
  comparatif: {
    to: "/alternative-helloasso",
    titre: "Comparez avant de choisir",
    texte:
      "Ce qui change vraiment entre Donoor et les autres plateformes : commission, abonnement, délai de versement, propriété des fonds.",
    bouton: "Voir le comparatif",
  },
  siteInternet: {
    to: "/site-internet",
    titre: "La page internet de votre association",
    texte:
      "Une page publique prête à l'emploi : présentation, actualités, dons, adhésions et billetterie réunis sur une seule adresse.",
    bouton: "Découvrir la page association",
  },
  mosquee: {
    to: "/collecte-dons-mosquee",
    titre: "La collecte pensée pour les mosquées",
    texte:
      "Dons ponctuels et réguliers, zakat, bornes et QR codes en salle de prière, reçus fiscaux conformes à la loi de 1905.",
    bouton: "Voir la solution",
  },
  sportive: {
    to: "/collecte-dons-association-sportive",
    titre: "Pour les clubs et associations sportives",
    texte:
      "Licences et cotisations encaissées en ligne, billetterie des matchs, sponsors et subventions suivis au même endroit.",
    bouton: "Voir la solution",
  },
  ecole: {
    to: "/collecte-dons-ecole",
    titre: "Pour les APE, APEL et coopératives scolaires",
    texte:
      "Kermesse, sorties, voyages scolaires : encaissez les participations des familles en ligne, sans avance de trésorerie.",
    bouton: "Voir la solution",
  },
  humanitaire: {
    to: "/collecte-dons-association-humanitaire",
    titre: "Pour les associations humanitaires",
    texte:
      "Collectes d'urgence lancées en quelques minutes, dons récurrents, reçus fiscaux automatiques et reversement direct.",
    bouton: "Voir la solution",
  },
  environnement: {
    to: "/collecte-dons-association-environnement",
    titre: "Pour les associations environnementales",
    texte:
      "Financez vos actions de terrain : cagnottes par projet, adhésions, dons récurrents et reçus fiscaux automatiques.",
    bouton: "Voir la solution",
  },
  culturelle: {
    to: "/collecte-dons-association-culturelle",
    titre: "Pour les associations culturelles",
    texte:
      "Billetterie des spectacles, adhésions, mécénat et dons : tout sur une seule plateforme, sans abonnement.",
    bouton: "Voir la solution",
  },
  collecte: {
    to: "/collecte-dons-association",
    titre: "Collectez des dons pour votre association",
    texte:
      "Page de don, QR codes, reçus fiscaux automatiques et versement direct sur votre compte. 0 € d'abonnement, 0 % de commission.",
    bouton: "Voir comment collecter",
  },
} as const satisfies Record<string, BlogCtaDestination>;

type CleDestination = keyof typeof DESTINATIONS;

/**
 * Règles ordonnées : la PREMIÈRE dont un motif apparaît dans le slug gagne.
 *
 * L'ordre est la règle de départage — du besoin le plus précis (un loto) au
 * plus général (collecter des dons). Toute nouvelle règle spécifique se place
 * donc EN HAUT, jamais en bas.
 */
const REGLES: Array<{ motifs: string[]; destination: CleDestination }> = [
  // ── Projets d'événement : les pages qui convertissent le mieux du site ──
  { motifs: ["tombola"], destination: "tombola" },
  { motifs: ["loto"], destination: "loto" },
  { motifs: ["kermesse"], destination: "kermesse" },
  { motifs: ["gala"], destination: "gala" },
  { motifs: ["vide-grenier", "bourse-aux-livres"], destination: "videGrenier" },
  { motifs: ["concert", "musique", "sacem"], destination: "concert" },

  // ── Publics précis (avant les thèmes génériques) ────────────────────────
  {
    motifs: ["mosquee", "zakat", "cultuel", "cultuelle", "ramadan", "sadaqa", "sadaqah", "loi-1905"],
    destination: "mosquee",
  },
  {
    motifs: ["sportive", "sportif", "club-sportif", "subventions-sport", "cnds", "fdva"],
    destination: "sportive",
  },
  {
    motifs: ["ecole", "scolaire", "ape-", "apel", "parents-eleves", "pedagogique", "educative"],
    destination: "ecole",
  },
  { motifs: ["humanitaire"], destination: "humanitaire" },
  { motifs: ["environnement"], destination: "environnement" },
  { motifs: ["culturelle"], destination: "culturelle" },

  // ── Fonctionnalités ─────────────────────────────────────────────────────
  { motifs: ["tap-to-pay", "apple-pay", "google-pay"], destination: "tapToPay" },
  { motifs: ["qr-code", "qr-codes", "borne-don"], destination: "qrCodes" },
  {
    motifs: ["recu-fiscal", "recus-fiscaux", "cerfa", "deduction-fiscale", "rescrit", "frais-benevolat"],
    destination: "recusFiscaux",
  },
  {
    motifs: ["adhesion", "adhesions", "cotisation", "cotisations", "adherents", "bulletin", "inscriptions-club"],
    destination: "adhesion",
  },
  {
    motifs: ["don-recurrent", "dons-recurrents", "don-ponctuel"],
    destination: "donRecurrent",
  },
  { motifs: ["cagnotte", "crowdfunding"], destination: "cagnotte" },
  {
    motifs: ["evenement-caritatif", "organiser-evenement", "billetterie"],
    destination: "billetterie",
  },
  { motifs: ["site-web", "logo"], destination: "siteInternet" },

  // ── Comparatifs : intention d'achat forte ───────────────────────────────
  {
    motifs: ["alternative", "donoor-vs", "migrer-helloasso", "concurrence", "custodial"],
    destination: "comparatif",
  },

  // ── Vie administrative de l'association → le logiciel ───────────────────
  {
    motifs: [
      "comptabilite", "tresorerie", "tresorier", "bilan", "budget", "plan-comptable",
      "compte-bancaire", "cheque-emploi", "statuts", "rna", "journal-officiel",
      "recepisse", "creer-association", "creer-ape", "dissoudre", "modifier-statuts",
      "responsabilite", "assurance", "rgpd", "ess-economie", "fondation",
      "interet-general", "micro-entreprise", "loi-finances", "assemblee-generale",
      "pv-assemblee", "benevole", "benevoles", "benevolat", "le-compte-asso",
      "tableau-bord", "newsletter",
    ],
    destination: "logiciel",
  },
];

/** Destination par défaut : la page de besoin la plus généraliste, jamais l'accueil. */
const DEFAUT: CleDestination = "collecte";

/**
 * Renvoie la page produit à proposer au lecteur d'un article.
 *
 * @param slug slug de l'article (ex. « organiser-loto-associatif-regles-2026 »)
 */
export function blogCtaTarget(slug: string | null | undefined): BlogCtaDestination {
  const s = (slug ?? "").toLowerCase();
  if (s) {
    for (const regle of REGLES) {
      if (regle.motifs.some((m) => s.includes(m))) return DESTINATIONS[regle.destination];
    }
  }
  return DESTINATIONS[DEFAUT];
}

/** Exporté pour les tests : permet de vérifier qu'aucune destination n'est morte. */
export const BLOG_CTA_DESTINATIONS = DESTINATIONS;
